/**
 * Fetch branches for a project. Tracks loading and error so UI can
 * distinguish "still fetching" from "failed" from "no repo configured".
 *
 * `error` carries the HTTP status when available — the branch picker uses
 * 404 to render a "no repo linked" hint instead of a generic failure.
 */

import { useEffect, useState } from "react";
import { fetchBranches, type BranchesResponse } from "@/api/branches";
import { translate } from "@/i18n";
import { hooksMessages } from "@/i18n/messages/hooks";

export interface UseBranchesResult {
  data: BranchesResponse | null;
  isLoading: boolean;
  error: { status: number | null; message: string } | null;
}

export function useBranches(
  projectId: string | null | undefined,
): UseBranchesResult {
  const [data, setData] = useState<BranchesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<UseBranchesResult["error"]>(null);

  useEffect(() => {
    if (!projectId) {
      setData(null);
      setIsLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    fetchBranches(projectId)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const maybe = err as { status?: number; data?: { error?: string } };
        const status = typeof maybe?.status === "number" ? maybe.status : null;
        const message =
          maybe?.data?.error ??
          (err instanceof Error
            ? err.message
            : translate(hooksMessages, "branchesLoadFailed"));
        setData(null);
        setError({ status, message });
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return { data, isLoading, error };
}
